// Datadog RUM view names. With hash routing every page has the same URL path, so Datadog's own
// naming would put the whole app in one view; views are started from `router.afterEach` instead.
// A view name is the route name, plus the product type for routes that serve several products
// (`Routes:VANPOOL`), or the legacy AngularJS state for the legacy iframe (`legacy:app.route.accounting`).
// It is the same identity a main-menu node has, so "shown in the menu but never opened" can be read
// off Datadog. Route names are a stable contract (menu data, `mainPage`, service worker): never
// put labels, paths or ids in a view name.

const PRODUCT_TYPES = ['SHUTTLE', 'VANPOOL', 'FLEXIRIDE_POOL', 'FLEXIRIDE_POOL_2', 'FLEXIRIDE_POOL_3'];

const AREA_BY_PRODUCT_TYPE = {
  SHUTTLE: 'shuttle',
  VANPOOL: 'vanpool',
  FLEXIRIDE_POOL: 'on-demand',
  FLEXIRIDE_POOL_2: 'on-demand',
  FLEXIRIDE_POOL_3: 'on-demand',
};

// Product area of every route, for grouping usage by product. `typed`: the route's path has a
// `:type` param, which goes into the view name and, unless `fixedArea`, sets the area. Only these
// routes use the type: the menu also passes `typeParam` to routes without one, where it is lost on
// reload. `defaultType`: the product the screen shows when the URL names none (screens that show
// every product then have none). `transit`: the route only forwards to another one, so it does not
// start a view. Unit tests keep this in step with router.js.
export const VIEW_CATALOG = {
  login: { area: 'auth', transit: true },
  AutoLogin: { area: 'auth', transit: true },
  updatePassword: { area: 'auth' },
  register: { area: 'auth' },
  LoginRedirector: { area: 'auth', transit: true },
  Root: { area: 'auth', transit: true },

  Home: { area: 'shuttle' },
  FlexiShuttleDashboardUs: { area: 'shuttle' },
  RouteDetail: { area: 'shuttle' },
  OptimizationDetail: { area: 'shuttle' },
  DriverVehicleMatching: { area: 'shuttle' },
  RouteSchedules: { area: 'shuttle' },
  RouteSchedulesV1: { area: 'shuttle' },
  Scheduling: { area: 'shuttle' },
  WalletTransactions: { area: 'shuttle' },
  ShuttleRiders: { area: 'shuttle' },
  ApsrDevices: { area: 'shuttle' },
  Workgroups: { area: 'shuttle', typed: true },
  Routes: { area: 'shuttle', typed: true, defaultType: 'SHUTTLE' },
  RouteStatuses: { area: 'shuttle', typed: true },
  Dispatch: { area: 'shuttle', typed: true },
  Reservations: { area: 'shuttle', typed: true },
  RouteSearch: { area: 'shuttle', typed: true },

  FlexiVanpoolDashboard: { area: 'vanpool' },

  FlexiOnDemandDashboard: { area: 'on-demand', typed: true, defaultType: 'FLEXIRIDE_POOL' },
  FlexiridePoolRequests: { area: 'on-demand', typed: true },
  FlexiRidePoolPlanning: { area: 'on-demand', typed: true },
  FlexiRidePoolTracking: { area: 'on-demand', typed: true },
  FlexiRidePoolAssignments: { area: 'on-demand', typed: true, defaultType: 'FLEXIRIDE_POOL' },

  flexiRideDashboard: { area: 'flexiride' },
  FlexiridePlanning: { area: 'flexiride' },
  FlexirideTracking: { area: 'flexiride' },
  driversCheckList: { area: 'flexiride' },
  FlexirideRequests: { area: 'flexiride', typed: true },
  FlexirideDrivers: { area: 'flexiride', typed: true },
  FlexirideVehicles: { area: 'flexiride', typed: true },
  ReservationConfirmation: { area: 'flexiride' },
  RequestConfirmation: { area: 'flexiride' },
  RequestApprove: { area: 'flexiride' },

  FlexicarDashboard: { area: 'flexicar' },
  FlexicarUsages: { area: 'flexicar' },
  FlexicarUsageDetail: { area: 'flexicar' },
  FlexicarDrivers: { area: 'flexicar' },
  FlexicarVehicles: { area: 'flexicar' },
  FlexicarVehicleDetail: { area: 'flexicar' },
  FlexicarParkings: { area: 'flexicar' },
  FlexicarRentals: { area: 'flexicar' },
  FlexicarTracking: { area: 'flexicar' },
  FlexicarRequests: { area: 'flexicar' },
  FlexicarPlanning: { area: 'flexicar' },

  CarpoolUserStatus: { area: 'carpool' },
  CarpoolApprovedMatches: { area: 'carpool' },
  carpoolGrid: { area: 'carpool' },

  flexiMileageGrid: { area: 'mileage' },

  OnboardingDashboard: { area: 'commute-analysis' },
  OnboardingRegistration: { area: 'commute-analysis' },
  OnboardingEmployees: { area: 'commute-analysis' },
  OnboardingSplash: { area: 'commute-analysis' },

  employeeRegistration: { area: 'employee-survey' },
  employeeDashboard: { area: 'employee-survey' },
  employeeSetHome: { area: 'employee-survey' },
  employeesurvey: { area: 'employee-survey' },
  employeeeditProfile: { area: 'employee-survey' },
  employeechangePassword: { area: 'employee-survey' },
  employeeupdateHomeAddress: { area: 'employee-survey' },

  EmployeeList: { area: 'riders' },
  GuaranteedRide: { area: 'riders' },

  DriverList: { area: 'fleet' },
  VehicleList: { area: 'fleet' },

  Stops: { area: 'stops' },
  Reports: { area: 'reports' },

  Company: { area: 'settings' },
  CompanyList: { area: 'settings' },
  ChangePassword: { area: 'account' },

  Tickets: { area: 'platform' },
  Survey: { area: 'platform' },
  notifications: { area: 'platform' },
  requestApprovalsGrid: { area: 'platform' },

  AgentChat: { area: 'flexiai' },

  SharedRideRequest: { area: 'rider-web', typed: true, fixedArea: true, defaultType: 'FLEXIRIDE_POOL' },
  MyAddressList: { area: 'rider-web' },
  FeedbackList: { area: 'rider-web' },
  RiderTripPlanner: { area: 'rider-web' },
  MyTrips: { area: 'rider-web' },
  RiderRouteSearch: { area: 'rider-web' },
  MyWallet: { area: 'rider-web' },

  HomeAddress: { area: 'rider-onboarding' },
  ApproveKvkk: { area: 'rider-onboarding' },
  SelectMainCampus: { area: 'rider-onboarding' },
  selectCompany: { area: 'rider-onboarding' },
  selectDepartment: { area: 'rider-onboarding' },
  workgroupShiftTime: { area: 'rider-onboarding' },
  PersonalInfo: { area: 'rider-onboarding' },

  legacy: { area: 'legacy' },
  FlexigoApp: { area: 'static' },

  Splash: { area: 'dev' },
  sseTest: { area: 'dev' },
  SSEDebugPanel: { area: 'dev' },
};

const LEGACY_STATE_RE = /^app\.[\w.*-]+$/;

function legacyArea(state) {
  if (state.startsWith('app.personel.')) return 'rider-web';
  if (/^app\.route\.(acc|route_tarihce)/.test(state)) return 'payments';
  return 'legacy';
}

// The catch-all `/` route renders the login screen and has no name
function routeName(route) {
  return route.name || (route.path === '/' ? 'Root' : 'unnamed');
}

/**
 * @param {{ name?: string, path?: string, params?: object }} route a vue-router route, or a
 *   location such as `menuItemRoute()` returns
 * @returns {{ name: string, context: object } | null} null for transit routes
 */
export function pageView(route) {
  const name = routeName(route);
  const entry = VIEW_CATALOG[name] || { area: 'unknown' };
  if (entry.transit) return null;

  const params = route.params || {};
  if (name === 'legacy') {
    const state = LEGACY_STATE_RE.test(params.route) ? params.route : 'other';
    return { name: `legacy:${state}`, context: { route_name: name, area: legacyArea(state) } };
  }

  const context = { route_name: name, area: entry.area };
  // Dashboard drill-downs pass the product as `?workgroupType=`; some screens default it themselves
  const rawType = entry.typed && (params.type || route.query?.workgroupType || entry.defaultType);
  if (!rawType) return { name, context };

  const type = PRODUCT_TYPES.includes(String(rawType).toUpperCase()) ? String(rawType).toUpperCase() : 'OTHER';
  context.product_type = type;
  if (!entry.fixedArea && AREA_BY_PRODUCT_TYPE[type]) context.area = AREA_BY_PRODUCT_TYPE[type];
  return { name: `${name}:${type}`, context };
}

/**
 * @param {object} route see `pageView`
 * @returns {string} the view name, also for transit routes
 */
export function viewName(route) {
  return pageView(route)?.name ?? routeName(route);
}
